import { formatDistanceToNow } from 'date-fns'
import { useQuery } from '@tanstack/react-query'
import {
  createFileRoute,
  useParams,
  Link,
  useNavigate,
} from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import {
  Loader2,
  TriangleAlert,
  Lock,
  ArrowLeft,
  GitCommitHorizontal,
} from 'lucide-react'
import { workspaceIdAtom } from '@/stores/workspaceStore'
import { trpc } from '@/lib/trpc'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { Badge } from '@deepguide-ai/ui/badge'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@deepguide-ai/ui/breadcrumb'
import { Button } from '@deepguide-ai/ui/button'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import { EvidencePack } from '@/components/compliance/EvidencePack'
import { SubmissionDetailsCard } from '@/components/compliance/SubmissionDetailsCard'
import MainHeader from '@/components/layout/MainHeader'
import { Main } from '@/components/main'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/submissions/$submissionId'
)({
  component: SubmissionDetailsPage,
})

function SubmissionDetailsPage() {
  const { submissionId } = useParams({ strict: false }) as {
    submissionId: string
  }
  const workspaceId = useAtomValue(workspaceIdAtom)
  return (
    <SharedSubmissionDetailsPage
      workspaceId={workspaceId || ''}
      submissionId={submissionId}
    />
  )
}

export function SharedSubmissionDetailsPage({
  workspaceId,
  submissionId,
  panelMode = false,
  onOpenTest,
}: {
  workspaceId: string
  submissionId: string
  panelMode?: boolean
  onOpenTest?: (testId: string) => void
}) {
  const navigate = useNavigate()
  const { canReview } = useWorkspacePermissions(workspaceId || undefined)

  // Fetch submission details
  const submissionQuery = useQuery({
    ...trpc.guidedTemplate.getSubmission.queryOptions({ id: submissionId }),
    enabled: !!submissionId,
  })

  const submission = submissionQuery.data?.submission
  const status = submissionQuery.data?.status

  // Check if this is the currently active submission
  const isActiveSubmission = status?.activeSubmissionId === submissionId
  const isApproved = submission?.reviewStatus === 'APPROVED'

  if (submissionQuery.isLoading) {
    if (panelMode) {
      return (
        <div className='text-muted-foreground flex h-full w-full items-center justify-center gap-2 text-sm'>
          <Loader2 className='h-6 w-6 animate-spin' />
        </div>
      )
    }
    return (
      <Main>
        <MainHeader />
        <div className='text-muted-foreground flex h-[50vh] w-full items-center justify-center gap-2 text-sm'>
          <Loader2 className='h-6 w-6 animate-spin' />
        </div>
      </Main>
    )
  }

  // Check if this is a permission denied error
  const isForbidden =
    submissionQuery.error?.message?.toLowerCase().includes('permission') ||
    submissionQuery.error?.message?.toLowerCase().includes('forbidden') ||
    submissionQuery.error?.data?.code === 'FORBIDDEN'

  // Extract metadata from FORBIDDEN error for breadcrumb display
  const errorCause = submissionQuery.error?.data?.cause as
    | {
        testId?: string
        testCode?: string
        revisionNumber?: number
      }
    | undefined
  const errorTestId = errorCause?.testId
  const errorTestCode = errorCause?.testCode
  const errorRevisionNumber = errorCause?.revisionNumber

  if (submissionQuery.error || !submission) {
    if (panelMode) {
      return (
        <div className='flex h-full w-full flex-col items-center justify-center gap-4'>
          <TriangleAlert className='text-muted-foreground h-8 w-8' />
          <p className='text-muted-foreground'>
            {submissionQuery.error?.message || 'Submission not found'}
          </p>
        </div>
      )
    }
    return (
      <Main>
        <MainHeader className='!mb-0'>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link
                    to='/workspaces/$workspaceId/tasks'
                    params={{ workspaceId: workspaceId || '' }}
                  >
                    My Tasks
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {errorTestId && errorTestCode && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link
                        to='/workspaces/$workspaceId/tests/$testId'
                        params={{
                          workspaceId: workspaceId || '',
                          testId: errorTestId,
                        }}
                      >
                        {errorTestCode}
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </>
              )}
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {errorRevisionNumber
                    ? `Submission #${errorRevisionNumber}`
                    : 'Submission'}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </MainHeader>
        <div className='mx-auto box-content flex max-w-4xl flex-col px-6 pt-6 pb-12'>
          {isForbidden ? (
            // AC-3: Better UI for access denied
            <div className='overflow-hidden rounded-md border'>
              <div className='border-b border-status-orange/30 bg-status-orange-bg px-4 py-3 text-status-orange-strong'>
                <div className='flex items-center gap-2'>
                  <Lock className='h-4 w-4 flex-shrink-0' />
                  <span className='text-sm font-semibold'>
                    Access Restricted
                  </span>
                </div>
                <p className='mt-1 text-xs opacity-90'>
                  You do not have permission to view this submission.
                </p>
              </div>
              <div className='space-y-4 p-6'>
                <p className='text-muted-foreground text-sm'>
                  This submission belongs to a task that is not assigned to you.
                  You can only view submissions for tasks you are assigned to,
                  or submissions you have created.
                </p>
                <Button
                  variant='outline'
                  onClick={() =>
                    navigate({
                      to: '/workspaces/$workspaceId/tasks',
                      params: { workspaceId: workspaceId || '' },
                    })
                  }
                >
                  <ArrowLeft className='h-4 w-4' />
                  Back to My Tasks
                </Button>
              </div>
            </div>
          ) : (
            // Generic error state
            <div className='flex h-[50vh] w-full flex-col items-center justify-center gap-4'>
              <TriangleAlert className='text-muted-foreground h-8 w-8' />
              <p className='text-muted-foreground'>
                {submissionQuery.error?.message || 'Submission not found'}
              </p>
              <Button
                variant='outline'
                onClick={() =>
                  navigate({
                    to: '/workspaces/$workspaceId/tasks',
                    params: { workspaceId: workspaceId || '' },
                  })
                }
              >
                <ArrowLeft className='h-4 w-4' />
                Back to My Tasks
              </Button>
            </div>
          )}
        </div>
      </Main>
    )
  }

  return (
    <Main className={panelMode ? 'h-full min-h-0 overflow-hidden' : undefined}>
      {!panelMode && (
        <MainHeader className='!mb-0'>
          <Breadcrumb>
            <BreadcrumbList>
              {status?.controlDefId ? (
                <>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link
                        to='/workspaces/$workspaceId/controls'
                        params={{ workspaceId: workspaceId || '' }}
                      >
                        Controls
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link
                        to='/workspaces/$workspaceId/controls/$controlDefId'
                        params={{
                          workspaceId: workspaceId || '',
                          controlDefId: status.controlDefId,
                        }}
                      >
                        {status.displayCode || status.controlId || 'Control'}
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </>
              ) : (
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link
                      to='/workspaces/$workspaceId/compliance'
                      params={{ workspaceId: workspaceId || '' }}
                    >
                      Tests
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              )}
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link
                    to='/workspaces/$workspaceId/tests/$testId'
                    params={{
                      workspaceId: workspaceId || '',
                      testId: status?.id || submission.statusId || '',
                    }}
                  >
                    {status?.testCode || 'Test'}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  Submission #{submission.revisionNumber || 1}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </MainHeader>
      )}
      <div className='h-full min-h-0 overflow-y-auto'>
        <div className='mx-auto box-content flex max-w-4xl flex-col px-8 pt-8 pb-12'>
          {!panelMode && (
            <button
              type='button'
              className='mb-4 inline-flex items-center gap-1.5 text-[12px] text-text-subtle transition-colors hover:text-text-body focus-visible:ring-2 focus-visible:ring-ring/20 focus-visible:outline-none'
              onClick={() => window.history.back()}
            >
              <ArrowLeft className='h-3.5 w-3.5' />
              Back
            </button>
          )}

          {/* Page-level header — title + status badges */}
          <div className='mb-5 flex flex-wrap items-center gap-2'>
            <h1 className='font-heading mt-0 !text-[24px] leading-[1.3] font-bold text-text-secondary'>
              Submission #{submission.revisionNumber || 1}
            </h1>
            {isActiveSubmission && (
              <Badge className='border-status-green/30 bg-status-green-bg text-status-green-strong'>
                Latest
              </Badge>
            )}
            {submission.reviewStatus === 'PENDING' &&
              (isActiveSubmission ? (
                <Badge className='bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'>
                  Awaiting Review
                </Badge>
              ) : (
                <Badge variant='outline' className='text-muted-foreground'>
                  Superseded
                </Badge>
              ))}
            {submission.reviewStatus === 'NEEDS_REVISION' && (
              <Badge className='bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'>
                Needs Revision
              </Badge>
            )}
            {isApproved && (
              <Badge className='bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'>
                Approved
              </Badge>
            )}
            {submission.reviewStatus === 'WITHDRAWN' && (
              <Badge variant='outline' className='text-muted-foreground'>
                Withdrawn
              </Badge>
            )}
          </div>

          {/* //Not active submission notice */}
          {!isActiveSubmission && status?.activeSubmissionId && (
            <div className='mb-6 rounded-md border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30'>
              <p className='text-sm text-amber-800 dark:text-amber-200'>
                This submission has been superseded by a newer version.{' '}
                <Link
                  to='/workspaces/$workspaceId/submissions/$submissionId'
                  params={{
                    workspaceId: workspaceId || '',
                    submissionId: status.activeSubmissionId,
                  }}
                  className='font-medium underline'
                >
                  View current submission →
                </Link>
              </p>
            </div>
          )}

          {/* Two-column layout — mirrors the submissions list page */}
          <div className='flex gap-6'>
            {/* Left rail — submission metadata */}
            <div className='w-32 shrink-0 pt-4 text-sm'>
              <div className='text-muted-foreground'>
                {submission.submittedAt
                  ? formatDistanceToNow(new Date(submission.submittedAt), {
                      addSuffix: true,
                    })
                  : '-'}
              </div>
              <div className='mt-1 flex items-center gap-1.5'>
                <MemberAvatar
                  person={{
                    image: submission.submittedByUser?.image,
                    name: submission.submittedByUser?.name,
                    email:
                      submission.submittedByUser?.email ||
                      submission.submittedByEmail,
                  }}
                  size='sm'
                />
                <span className='max-w-[80px] truncate text-xs'>
                  {submission.submittedByUser?.name ||
                    submission.submittedByUser?.email ||
                    submission.submittedByEmail ||
                    'Unknown'}
                </span>
              </div>
              <div className='text-muted-foreground mt-1 flex items-center gap-1'>
                <GitCommitHorizontal className='h-3 w-3' />
                <span className='font-mono text-xs'>
                  {submission.id.slice(0, 7)}
                </span>
              </div>
            </div>

            {/* Right column — main card */}
            <div className='min-w-0 flex-1'>
              <SubmissionDetailsCard
                submission={submission}
                isActiveSubmission={isActiveSubmission}
                workspaceId={workspaceId || ''}
                testTitle={status?.testTitle || submission.topicTitle}
                testCode={status?.testCode}
                testId={status?.id || submission.statusId}
                onNavigateToTest={onOpenTest}
                showDetailedScores={canReview}
                showViewButton={false}
                hideTitle
                hideSubmitter
                evidenceDocLinks={status?.evidenceDocLinks}
              />
              {submission.evidencePack?.id && (
                <EvidencePack evidencePackId={submission.evidencePack?.id} />
              )}
            </div>
          </div>
        </div>
      </div>
    </Main>
  )
}
